/**
 * RealOhlcPrimitive — reveals the REAL bar OHLC hidden by smoothed candle types
 * (Heikin-Ashi / CSH / CSL / CLH). Those candle types draw synthetic open/high/
 * low/close, so the real bar's CLOSE is never shown, and the real LOW (or HIGH) is
 * hidden whenever the smoothed candle has no lower (or upper) wick.
 *
 * For each bar it draws, as a distinct "real OHLC bar" ON TOP of the candle so it
 * never gets hidden behind / blended with the smoothed wick:
 *   1. small CAPS (horizontal ticks centered on the bar) at the REAL high and REAL
 *      low — pins the true extremes even when they sit inside a longer smoothed wick;
 *   2. a faint thin connector from real low → real high so the caps read as one bar;
 *   3. a horizontal tick at the REAL close on the RIGHT (up/down coloured) — no wick
 *      ever conveys the real close.
 *
 * These belong to the wick layer: the chart only feeds points when wicks are on,
 * so they disappear when wicks are toggled off.
 */
import type {
  ISeriesPrimitive,
  IPrimitivePaneView,
  IPrimitivePaneRenderer,
  SeriesAttachedParameter,
  Time,
  IChartApi,
  ISeriesApi,
  SeriesType,
} from 'lightweight-charts';
import type { CanvasRenderingTarget2D } from 'fancy-canvas';

export interface RealOhlcPoint {
  time: number;
  high: number;   // REAL bar high
  low: number;    // REAL bar low
  close: number;  // REAL bar close
  up: boolean;    // realClose >= realOpen (tick colour)
  // De-clutter: only show a high/low cap when the DRAWN candle has NO wick on that
  // side (the wick already reveals that extreme, so the cap would be redundant).
  // Close tick is always drawn (a smoothed candle never shows the real close).
  showHigh: boolean;
  showLow: boolean;
}

export interface RealOhlcColors {
  range: string;  // faint connector low→high
  cap: string;    // real high / low caps (distinct, bright)
  up: string;     // close tick when up
  down: string;   // close tick when down
}

/** Real-OHLC bar (caps at high/low + faint connector + close tick) — on top. */
class RealOhlcRenderer implements IPrimitivePaneRenderer {
  constructor(
    private readonly points: RealOhlcPoint[],
    private readonly chart: IChartApi,
    private readonly series: ISeriesApi<SeriesType>,
    private readonly colors: RealOhlcColors,
  ) {}
  draw(target: CanvasRenderingTarget2D): void {
    if (!this.points.length) return;
    const ts = this.chart.timeScale();
    const barSpacing = Number(ts.options().barSpacing) || 6;
    const capHalf = Math.max(1.5, Math.min(barSpacing * 0.32, 4.5));  // CSS px each side of centre
    const closeLen = Math.max(2, Math.min(barSpacing * 0.42, 6.5));   // CSS px, right of centre
    target.useBitmapCoordinateSpace(({ context: ctx, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      ctx.save();
      for (const p of this.points) {
        const x = ts.timeToCoordinate(p.time as Time);
        const yH = this.series.priceToCoordinate(p.high);
        const yL = this.series.priceToCoordinate(p.low);
        const yC = this.series.priceToCoordinate(p.close);
        if (x == null) continue;
        const px = Math.round(Number(x) * hpr) + 0.5;
        const ch = capHalf * hpr;
        // faint connector low→high — only when at least one cap is shown (else it's
        // just a faint duplicate of the candle's own wick = clutter).
        if ((p.showHigh || p.showLow) && yH != null && yL != null) {
          ctx.strokeStyle = this.colors.range;
          ctx.lineWidth = Math.max(1, Math.round(hpr));
          ctx.beginPath(); ctx.moveTo(px, Number(yH) * vpr); ctx.lineTo(px, Number(yL) * vpr); ctx.stroke();
        }
        // real-high / real-low caps — drawn only on the side WITHOUT a candle wick.
        ctx.strokeStyle = this.colors.cap;
        ctx.lineWidth = Math.max(1, Math.round(1.4 * vpr));
        if (p.showHigh && yH != null) { const y = Math.round(Number(yH) * vpr) + 0.5; ctx.beginPath(); ctx.moveTo(px - ch, y); ctx.lineTo(px + ch, y); ctx.stroke(); }
        if (p.showLow && yL != null) { const y = Math.round(Number(yL) * vpr) + 0.5; ctx.beginPath(); ctx.moveTo(px - ch, y); ctx.lineTo(px + ch, y); ctx.stroke(); }
        // real-close tick (right of centre, up/down coloured)
        if (yC != null) {
          ctx.strokeStyle = p.up ? this.colors.up : this.colors.down;
          ctx.lineWidth = Math.max(1, Math.round(1.7 * vpr));
          const y = Math.round(Number(yC) * vpr) + 0.5;
          ctx.beginPath(); ctx.moveTo(px, y); ctx.lineTo(px + closeLen * hpr, y); ctx.stroke();
        }
      }
      ctx.restore();
    });
  }
}

class RealOhlcPaneView implements IPrimitivePaneView {
  constructor(private readonly parent: RealOhlcPrimitive) {}
  zOrder() { return 'top' as const; } // above candles → always visible
  renderer(): IPrimitivePaneRenderer | null {
    const { chart, series, colors, points } = this.parent;
    return chart && series ? new RealOhlcRenderer(points, chart, series, colors) : null;
  }
}

export class RealOhlcPrimitive implements ISeriesPrimitive<Time> {
  points: RealOhlcPoint[] = [];
  colors: RealOhlcColors;
  chart: IChartApi | null = null;
  series: ISeriesApi<SeriesType> | null = null;
  private readonly _paneViews: IPrimitivePaneView[];

  constructor(colors: RealOhlcColors, points: RealOhlcPoint[] = []) {
    this.colors = colors;
    this.points = points;
    this._paneViews = [new RealOhlcPaneView(this)];
  }

  attached(params: SeriesAttachedParameter<Time>): void {
    this.chart = params.chart as IChartApi;
    this.series = params.series as ISeriesApi<SeriesType>;
  }
  detached(): void { this.chart = null; this.series = null; }

  updateAllViews(): void { /* renderers read fresh from this.points */ }
  paneViews(): readonly IPrimitivePaneView[] { return this._paneViews; }

  setData(points: RealOhlcPoint[]): void { this.points = points; this.chart?.applyOptions({}); }
  setColors(colors: RealOhlcColors): void { this.colors = colors; this.chart?.applyOptions({}); }
}
